import { useEffect, useMemo } from 'react';
import CancellableRequest from '.';
/**
 * @description
 * Hook for creating request(s) in components/custom hooks
 *
 * It automatically cancels previous pending request on unmount
 *
 * @example
 * //Usage in useEffect. Request cancels on each call and on unmount
 * const MyComponent = () => {
 *  const request = useCancellableRequest();
 *
 *  useEffect(() => {
 *    request.call(getCarriers, { portal_id: portalId })
 *      .then(res => setCarriers(res));
 *  }, [portalId])
 * }
 *
 * @example
 * //Usage in components on user action (click, change, etc).
 * const MyComponent = () => {
 *  const request = useCancellableRequest();
 *
 *  const handleChange = ({ target: { value }} ) => {
 *     request.call(getCarriers, { portal_id: value })
 *      .then(res => setCarriers(res))
 *  }
 * return <Select onChange={handleChange}/>
 * }
 *
 * @example
 * //Usage in component for combined request (multiple requests - same token)
 * const MyComponent = () => {
 *  const combinedRequest = useCancellableRequest();
 *
 *  useEffect(() => {
 *    combinedRequest.cancel();
 *    const abortSignal = combinedRequest.abortController?.signal;
 *
 *    getCarriers({ portal_id: value }, abortSignal)
 *      .then(res => setCarriers(res))
 *    getDevices({ portal_id: value }, abortSignal)
 *      .then(res => setDevices(res))
 *  }, [portalId])
 * }
 */

export const useCancellableRequest = () => {
  const request = useMemo(() => new CancellableRequest(), []);

  useEffect(() => {
    return () => {
      request.cancel();
    };
  }, [request]);

  return request;
};
